import React from "react";
import PropTypes from "prop-types";
import InvestorLogo from 'components/InvestorLogo';
import Icon from 'components/Icon';
import InvestorMainMenu from 'components/InvestorMainMenu';
import InvestorMainMenuMobile from 'components/InvestorMainMenuMobile';
import InvestorSecondaryMenu from 'components/InvestorSecondaryMenu';
import LanguageSelector from 'components/LanguageSelector';
import SearchForm from 'components/SearchForm';
import InvestorLink from 'components/InvestorLink';
import Link from '../Models/Link/Link';
import LanguageLink from '../Models/LanguageLink/LanguageLink';
import staticLogo from 'assets/logo.svg';
import cn from 'classnames';
import './InvestorHeader.scss';

const SCROLL_THRESHOLD = 70;
const MOBILE_MENU_OPENED_CLASSNAME = "investor-page-mobile-menu-opened";

class InvestorHeader extends React.PureComponent {
    constructor(props) {
        super(props);
    }

    static propTypes = {
        logoLink: PropTypes.string.isRequired,
        logoAltText: PropTypes.string.isRequired,
        investorLogo: PropTypes.shape(Link.propTypes),
        backToMainSite: PropTypes.shape(Link.propTypes),
        pageHeading: PropTypes.string,
        mainMenu: PropTypes.shape(InvestorMainMenu.propTypes),
        secondaryMenu: PropTypes.shape(InvestorSecondaryMenu.propTypes),
        languages: PropTypes.arrayOf(PropTypes.shape(LanguageLink.propTypes)),
        quickSearch: PropTypes.shape({
            searchPageUrl: PropTypes.string.isRequired,
        }),
        lang: PropTypes.shape({
            openQuickSearchFormBtnAriaLabel: PropTypes.string.isRequired,
            closeQuickSearchFormBtnAriaLabel: PropTypes.string.isRequired,
            searchHint: PropTypes.string.isRequired,
            searchLabel: PropTypes.string.isRequired,
        }).isRequired,
    };

    state = {
        isHeaderHidden: false,
        isMobileMenuOpened: false,
        isActive: null,
        disableScrollBehavior: false
    };

    componentDidMount() {
        this.bodyElement = document.body;
        window.addEventListener("scroll", this.handleScroll);
        window.addEventListener("keydown", this.handleKeyDown);

        window.lastScrollY = 0;
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("keydown", this.handleKeyDown);
    }

    handleScroll = () => {
        if (this.state.disableScrollBehavior) {
            return;
        }

        const isHeaderHidden =
            window.scrollY > window.lastScrollY &&
            window.scrollY > SCROLL_THRESHOLD &&
            !this.state.isMobileMenuOpened;

        if (
            this.state.isHeaderHidden !== isHeaderHidden
        ) {
            this.setState({ isHeaderHidden });
        }

        this.bodyElement.classList.toggle("investor-header-hidden", isHeaderHidden);

        window.lastScrollY = window.scrollY;
    };

    handleKeyDown = (event) => {
        if (event.key === "Escape") {
            this.setState({ isActive: null });
        }
    };

    handleSearchButtonClick = (id) => {
        this.setState({
            isActive: this.state.isActive === id ? null : id,
        });
    };

    handleCurtainClick = () => {
        this.setState({ isActive: null });
    };

    handleToggleMobileMenu = () => {
        this.setState((prevState) => {
            const isMobileMenuOpened = !prevState.isMobileMenuOpened;
            this.bodyElement.classList.toggle(MOBILE_MENU_OPENED_CLASSNAME, isMobileMenuOpened);
            
            return { isMobileMenuOpened };
        });
    };

    allowHeaderHidingOnScrollHandler = (allowed) => {
        this.setState({ disableScrollBehavior: !allowed });
    };

    render() {
        const {
            logoLink,
            logoAltText,
            investorLogo,
            mainMenu,
            languages,
            quickSearch,
            lang,
            backToMainSite,
            pageHeading,
            secondaryMenu,
        } = this.props;
        const {
            isHeaderHidden,
            isMobileMenuOpened,
            isActive,
        } = this.state;

        return (
            <div className="investor-header">
                <header
                    className={cn("investor-header__header-element", {
                        "investor-header__header-element_hidden": isHeaderHidden,
                        "investor-header__header-element_mobile-menu-opened": isMobileMenuOpened,
                        "investor-header__header-element_active": isActive,
                    })}
                >
                    {isActive !== null && (
                        <div
                            className="investor-header__curtain"
                            onClick={this.handleCurtainClick}
                        />
                    )}
                    <div className="investor-header__bg-wrapper">
                        <div className="investor-header__main-content">
                            <div className="investor-header__logos">
                                <InvestorLogo
                                    url={logoLink}
                                    image={{
                                        url: staticLogo,
                                        altText: logoAltText,
                                    }}
                                />
                                {investorLogo && (
                                    <a
                                        className="investor-header__investor-logo"
                                        href={investorLogo.url}
                                        target={
                                            investorLogo.openInNewWindow
                                                ? "_blank"
                                                : null
                                        }
                                    >
                                        {investorLogo.linkText}
                                    </a>
                                )}
                            </div>
                            <button
                                className="investor-header__mobile-menu-toggle"
                                onClick={this.handleToggleMobileMenu}
                            >
                                <Icon
                                    icon="hamburger"
                                    className="investor-header__mobile-menu-toggle-icon"
                                />
                            </button>
                            <div className="investor-header__actions-area">
                                {backToMainSite && (
                                    <div className="investor-header__back-navigation-link-holder investor-header__back-navigation-link-holder_mobile">
                                        <InvestorLink
                                            {...backToMainSite}
                                            leftIconName="arrow-line-left"
                                            className="investor-header__back-navigation-link investor-header__back-navigation-link_light"
                                        />
                                    </div>
                                )}
                                {mainMenu && (
                                    <InvestorMainMenu
                                        {...mainMenu}
                                        className="investor-header__main-menu"
                                        onLinkClick={this.handleToggleMobileMenu}
                                    />
                                )}
                                <div className="investor-header__actions">
                                    {languages && languages.length > 0 ? (
                                        <LanguageSelector
                                            languages={languages}
                                            isActive={isActive}
                                            className="investor-header__language-selector"
                                            handleSelectorClick={() =>
                                                this.handleSearchButtonClick(
                                                    "language"
                                                )
                                            }
                                        />
                                    ) : null}
                                    {quickSearch && (
                                        <SearchForm
                                            inputPlaceholder={lang.searchHint}
                                            isActive={isActive}
                                            searchLabel={lang.searchLabel}
                                            searchLink={quickSearch.searchPageUrl}
                                            openQuickSearchFormBtnAriaLabel={
                                                lang.openQuickSearchFormBtnAriaLabel
                                            }
                                            closeQuickSearchFormBtnAriaLabel={
                                                lang.closeQuickSearchFormBtnAriaLabel
                                            }
                                            className="investor-header__search-form"
                                            searchClassName="investor-header__search"
                                            handleSearchButtonClick={() =>
                                                this.handleSearchButtonClick(
                                                    "search"
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {secondaryMenu && (
                        <InvestorSecondaryMenu {...secondaryMenu} isHeaderHidden={isHeaderHidden} allowHeaderHidingOnScroll={this.allowHeaderHidingOnScrollHandler} />
                    )}
                </header>
                {backToMainSite && !secondaryMenu && (
                    <div className="investor-header__back-navigation-link-holder">
                        <InvestorLink
                            {...backToMainSite}
                            leftIconName="arrow-line-left"
                            className="investor-header__back-navigation-link"
                        />
                    </div>
                )}
                <div className="investor-header__page-info">
                    {pageHeading && (
                        <h1 className="investor-header__heading">
                            {pageHeading}
                        </h1>
                    )}
                    {mainMenu && !secondaryMenu && (
                        <InvestorMainMenuMobile {...mainMenu} />
                    )}
                </div>
            </div>
        );
    }
}

export default InvestorHeader;




// WEBPACK FOOTER //
// ./src/components/InvestorHeader/InvestorHeader.jsx